import { Text, Box, Group, Tooltip, SimpleGrid } from '@mantine/core';
import {
  TasteSummary,
  AxisWeight,
  INGREDIENT_CATEGORIES,
  ingredientDisplayName,
} from '../../types/tasteProfile';

type IngredientGridProps = {
  ingredientWeights: Record<string, AxisWeight>;
  summary: TasteSummary;
};

const PREFERRED_THRESHOLD = 5;
const AVOIDED_THRESHOLD = -5;

const classify = (
  key: string,
  summary: TasteSummary,
  w: AxisWeight | undefined
): 'preferred' | 'avoided' | 'neutral' | 'undiscovered' => {
  if (summary.preferredIngredients.includes(key)) return 'preferred';
  if (summary.avoidedIngredients.includes(key)) return 'avoided';
  if (!w) return 'undiscovered';
  const net = w.posWeight - w.negWeight;
  if (net >= PREFERRED_THRESHOLD) return 'preferred';
  if (net <= AVOIDED_THRESHOLD) return 'avoided';
  return 'neutral';
};

const chipStyle = (
  key: string,
  summary: TasteSummary,
  ingredientWeights: Record<string, AxisWeight>,
  maxAbs: number
): { bg: string; fg: string } => {
  const w = ingredientWeights[key];
  const cls = classify(key, summary, w);

  if (cls === 'preferred' || cls === 'avoided') {
    const ratio = w ? Math.abs(w.posWeight - w.negWeight) / Math.max(maxAbs, 0.01) : 0;
    const color = cls === 'preferred' ? 'green' : 'red';
    const shade = ratio >= 0.7 ? 6 : ratio >= 0.3 ? 4 : 2;
    const fg = shade >= 4 ? 'white' : `var(--mantine-color-${color}-7)`;
    return { bg: `var(--mantine-color-${color}-${shade})`, fg };
  }
  if (cls === 'neutral') {
    return { bg: 'var(--mantine-color-gray-2)', fg: 'var(--mantine-color-gray-6)' };
  }
  return { bg: 'var(--mantine-color-gray-1)', fg: 'var(--mantine-color-gray-5)' };
};

const BarTooltip = ({ label, w, maxVal }: { label: string; w: AxisWeight | undefined; maxVal: number }) => {
  if (!w || (w.posWeight === 0 && w.negWeight === 0)) return <>No data</>;
  return (
    <Box style={{ fontSize: 11, fontWeight: 500 }}>
      <div style={{ fontWeight: 700, marginBottom: 3 }}>{label}</div>
      {[
        { name: 'Preferred', value: w.posWeight, color: 'var(--mantine-color-green-5)' },
        { name: 'Avoided', value: w.negWeight, color: 'var(--mantine-color-red-4)' },
      ].map(({ name, value, color }) => (
        <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
          <span style={{ width: 52, flexShrink: 0 }}>{name}</span>
          <div style={{ width: 60, height: 6, backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ width: `${(value / maxVal) * 100}%`, height: '100%', backgroundColor: color, borderRadius: 3 }} />
          </div>
        </div>
      ))}
    </Box>
  );
};

/**
 * Alphabetical by the rendered name, matching PreferencePillStrip.
 *
 * Sorts on `ingredientDisplayName(key)` rather than the key, because the two
 * diverge: "cheese_generic" renders as "Cheese" and "white_fish" as "White
 * fish", so key order looks scrambled to a reader.
 *
 * The CATEGORY order is left alone on purpose. Those are semantic groupings
 * ("Proteins" next to "Plant Proteins"), and alphabetising the headings would
 * split related groups apart for no lookup benefit — you scan headings, not
 * hunt through them.
 */
const sortByDisplayName = (keys: readonly string[]): string[] =>
  [...keys].sort((a, b) =>
    ingredientDisplayName(a).localeCompare(ingredientDisplayName(b), undefined, {
      sensitivity: 'base',
    })
  );

export const IngredientGrid = ({ ingredientWeights, summary }: IngredientGridProps) => {
  const allKeys = Object.values(INGREDIENT_CATEGORIES).flat();
  const maxAbs = Math.max(
    ...allKeys.map((key) => {
      const w = ingredientWeights[key];
      return w ? Math.abs(w.posWeight - w.negWeight) : 0;
    }),
    0.01
  );

  const maxVal = Math.max(
    ...allKeys.map((key) => {
      const w = ingredientWeights[key];
      return w ? Math.max(w.posWeight, w.negWeight) : 0;
    }),
    0.01
  );

  return (
    <Box>
      <Text size="md" fw={600} mb="xs">
        Ingredient map
      </Text>

      {/* Legend */}
      <Box style={{ display: 'inline-flex', backgroundColor: 'var(--mantine-color-gray-0)', borderRadius: 8, padding: '6px 12px' }} mb="sm">
        <Group gap="md">
          <Group gap={4}>
            <Box style={{ width: 12, height: 12, borderRadius: 3, backgroundColor: 'var(--mantine-color-green-6)' }} />
            <Text size="xs" c="dimmed">Preferred</Text>
          </Group>
          <Group gap={4}>
            <Box style={{ width: 12, height: 12, borderRadius: 3, backgroundColor: 'var(--mantine-color-red-6)' }} />
            <Text size="xs" c="dimmed">Avoided</Text>
          </Group>
          <Group gap={4}>
            <Box style={{ width: 12, height: 12, borderRadius: 3, backgroundColor: 'var(--mantine-color-gray-2)' }} />
            <Text size="xs" c="dimmed">Neutral</Text>
          </Group>
          <Group gap={4}>
            <Box style={{ width: 12, height: 12, borderRadius: 3, backgroundColor: 'var(--mantine-color-gray-1)' }} />
            <Text size="xs" c="dimmed">Undiscovered</Text>
          </Group>
        </Group>
      </Box>

      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
      {Object.entries(INGREDIENT_CATEGORIES).map(([category, keys]) => (
        <Box key={category}>
          <Text size="xs" fw={600} c="dimmed" mb={4}>
            {category}
          </Text>
          <Box style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 4px' }}>
            {sortByDisplayName(keys).map((key) => {
              const { bg, fg } = chipStyle(key, summary, ingredientWeights, maxAbs);
              const displayName = ingredientDisplayName(key);

              return (
                <Tooltip key={key} label={<BarTooltip label={displayName} w={ingredientWeights[key]} maxVal={maxVal} />} withArrow events={{ hover: true, focus: true, touch: true }}>
                  <Box
                    style={{
                      backgroundColor: bg,
                      color: fg,
                      borderRadius: 16,
                      padding: '4px 10px',
                      fontSize: 11,
                      fontWeight: 600,
                      lineHeight: 1.3,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {displayName}
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        </Box>
      ))}
      </SimpleGrid>
    </Box>
  );
};
